import { useCallback, useRef, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Shared by the Orders and Invoices forms: both build lines from the product
// catalogue (Products Management) and price them from the Pricing module, so
// the rules for "what does this line cost" live in one place.

// The product's active price record wins; inside it a wholesale tier takes over
// once the quantity reaches its minimum. Products with no price record yet fall
// back to the retail price kept on the product itself.
export const priceFor = (product, activePrice, quantity) => {
  if (activePrice) {
    const tier = (activePrice.wholesale_tiers || [])
      .filter((t) => quantity >= (t.min_qty || 0))
      .sort((a, b) => (b.min_qty || 0) - (a.min_qty || 0))[0];
    if (tier) return { unit_price: tier.price || 0, price_id: activePrice.id, tier };
    return { unit_price: activePrice.retail_price || 0, price_id: activePrice.id, tier: null };
  }
  return { unit_price: product?.retail_price || 0, price_id: null, tier: null };
};

// One line of guidance under a form row: the catalogue price, which tier the
// quantity has unlocked, or how many more units unlock the next one.
export const priceHintText = (product, activePrice, quantity) => {
  if (!product) return null;
  const { tier } = priceFor(product, activePrice, quantity);
  const base = activePrice ? activePrice.retail_price || 0 : product.retail_price || 0;
  const parts = [`Catalogue price Tsh ${base.toLocaleString()}`];
  if (tier) {
    parts.push(`wholesale tier ${tier.min_qty}+ applied (Tsh ${(tier.price || 0).toLocaleString()})`);
  } else if (activePrice && (activePrice.wholesale_tiers || []).length) {
    const next = [...activePrice.wholesale_tiers]
      .sort((a, b) => (a.min_qty || 0) - (b.min_qty || 0))
      .find((t) => quantity < (t.min_qty || 0));
    if (next) parts.push(`${next.min_qty}+ units → Tsh ${(next.price || 0).toLocaleString()}`);
  }
  return parts.join(' · ');
};

// Prices live in their own versioned records, so each picked product is fetched
// once and cached for the life of the form.
export function usePriceBook() {
  const cache = useRef({});
  const [priceBook, setPriceBook] = useState({});

  const loadActivePrice = useCallback(async (productId) => {
    if (productId in cache.current) return cache.current[productId];
    let active = null;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/products/${productId}/prices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      active = (response.data || []).find((p) => p.status === 'active') || null;
    } catch (error) {
      console.error('Failed to fetch product prices');
    }
    cache.current[productId] = active;
    setPriceBook({ ...cache.current });
    return active;
  }, []);

  return { priceBook, loadActivePrice };
}

// Active products, newest catalogue state, for the line pickers.
export async function fetchCatalogue() {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API}/products`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return (response.data || []).filter((p) => p.is_active !== false);
  } catch (error) {
    console.error('Failed to fetch products');
    return [];
  }
}

// Label used in every product dropdown.
export const productLabel = (product) =>
  `${product.name} - ${product.category} (Tsh ${(product.retail_price || 0).toLocaleString()})`;
